/**
 * @file This file contains a utility function for fetching data about file objects from the Autodesk Construction Cloud API.
 * It orchestrates a series of API calls to get document metadata, GUIDs, and specific object data, and then updates the
 * application state with the fetched data.
 */
import {getDocsMetaData} from '../api/itemDataService';
import {getGuid} from '../api/guidService';
import {getSpecificObjectsData} from "../api/specificObjectsDataService";

/**
 * Fetches data about file objects from the Autodesk Construction Cloud API.
 * @param {object} params - The parameters for the function.
 * @param {string} params.accAccessToken - The access token for the Autodesk Construction Cloud API.
 * @param {string} params.selectedProjectId - The ID of the selected project.
 * @param {object} params.accCurrentNode - The currently selected node in the ACC tree.
 * @param {object} params.selectedAccAssetImportConfig - The configuration for the asset import.
 * @param {function} params.setFileObjectsData - A function to update the state with the fetched data.
 */
export const fetchFileObjectsData = async ({accAccessToken, selectedProjectId, accCurrentNode,selectedAccAssetImportConfig, setFileObjectsData}) => {

    let namePrefix = selectedAccAssetImportConfig?.attributes?.namePrefix;
    console.log('namePrefix:', namePrefix);

    /**
     * Fetches the GUIDs for a given derivative ID.
     * @param {string} derivativeId - The ID of the derivative.
     * @returns {Promise<object>} A promise that resolves to the fetched GUIDs.
     */
    const fetchGuid = async (derivativeId) => {
        const itemUrn = accCurrentNode.id;
        console.log('fetching metadata for:', itemUrn);
        try {
            const fetchedGuids = await getGuid({
                accessToken: accAccessToken,
                derivativeId: derivativeId,
            });
            console.log('Fetched item guids:', fetchedGuids);
            return fetchedGuids;
        } catch (err) {
            console.error('Error during fetching versions:', err);
        }
    };

    /**
     * Fetches specific object data for a given derivative and GUID.
     * @param {string} derivativeId - The ID of the derivative.
     * @param {string} guid - The GUID of the object.
     */
    const fetchObjectsData = async (derivativeId, guid) => {


        try {
            const fetchedObjectData = await getSpecificObjectsData({
                accessToken: accAccessToken,
                derivativeId: derivativeId,
                guid: guid,
                // TODO: maybe in the config file choice of $contains or $prefix, e.g. for nwd files use file extension nwd
                queryBody: {
                    "query": {
                        "$prefix": [
                            "name",
                            namePrefix
                        ]
                    },
                    "fields": [
                        "objectid",
                        "name",
                        "properties.*"
                    ],
                    "pagination": {
                        "offset": 0,
                        "limit": 500
                    },
                    "payload": "text"

                }
            });
            setFileObjectsData(fetchedObjectData);
            console.log('Fetched object data:', fetchedObjectData);


        } catch (err) {
            console.error('Error during fetching specific objects data:', err);
        }
    };

    /**
     * Fetches the item data for a given node, and then fetches the object data.
     * @param {object} accCurrentNode - The currently selected node in the ACC tree.
     */
    const fetchItemData = async (accCurrentNode) => {

        const itemUrn = accCurrentNode.id;
        // console.log('fetching metadata for:', itemUrn);
        try {
            const fetchedData = await getDocsMetaData({
                accessToken: accAccessToken,
                selectedProjectId,
                itemUrn: itemUrn,
            });


            // TODO to get latest version: projects/:project_id/items/:item_id/tip
            const derivativeId = fetchedData.included[0].relationships.derivatives.data.id;
            // console.log('Fetched item derivative:', derivativeId);
            const fetchedGuids = await fetchGuid(derivativeId);
            console.log('Fetched item guids:', fetchedGuids);
            // TODO: define selectbox for guids or rule and config file
            const guid = fetchedGuids.data.metadata[0].guid;
            console.log('Fetched item derivativeId, guid:', derivativeId, guid);
            const fetchedObjectData = await fetchObjectsData(derivativeId, guid);
            console.log('Fetched data for specific objects:', fetchedObjectData);
        } catch (err) {
            console.error('Error during fetching data for specific objects:', err);

        }
    };

    // Call fetchItemData with accCurrentNode as argument
    await fetchItemData(accCurrentNode);
};